import { bianka } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { Click, ClickType, ElementType, Impression } from '@fiverr-private/bianka_js';
import { ClickedBriefCreationDrawer } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/dynamic_matches/clicked_brief_creation_drawer';
import { PastedBriefCreationInput } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/dynamic_matches/pasted_brief_creation_input';
import { ViewedBriefCreationEducation } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/dynamic_matches/viewed_brief_creation_education';
import { ClickedBriefCreationSectionAction } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/dynamic_matches/clicked_brief_creation_section_action';
import {
    BriefFloatingFormPopup,
    BriefFloatingFormPopup_TriggeredBy as TriggeredBy,
} from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/brief_floating_form/brief_floating_form_popup';
import { BriefSectionCtaType } from '@fiverr-private/ai_brief';
import { Step } from '../constants';
import { TriggerScenario } from '../components/BriefFloatingFormPopup/types';
import { BriefCreationVariant } from '../context/BriefCreationContext';

const sharedImpData = {
    level: 1,
    visibleThroughScroll: false,
    positionInParent: 1,
    isLeaf: true,
    sizeInParent: 0,
};

// Mapping of step to impression type
const mapStepToImpression = {
    [Step.Creation]: 'provide_input',
    [Step.Disambiguation]: 'disambiguation',
    [Step.Edit]: 'brief_editor',
    [Step.Finish]: 'posted_brief',
    [Step.ProMigration]: 'pro_migration',
    [Step.Error]: 'error',
    [Step.Rephrase]: 'insufficient_info',
    [Step.ComingSoon]: 'coming_soon',
};

const mapTriggerScenarioToTriggerdBy = {
    [TriggerScenario.MANUALLY]: TriggeredBy.MANUALLY,
    [TriggerScenario.INTENSIVE_SCROLLING]: TriggeredBy.INTENSIVE_SCROLLING,
    [TriggerScenario.FIRST_TIME]: TriggeredBy.FIRST_TIME,
};

export enum BffPopupElementNames {
    CLOSE_POPUP = 'X',
    EDIT_YOUR_BRIEF = 'edit_your_brief',
    SEE_HOW_IT_WORKS = 'see_how_it_works',
}

export const DEFAULT_BI_PARENT_ELEMENT = 'drawer';
export const ONBOARDING_ROUTING_BI_PARENT_ELEMENT = 'full_page';

/**
 * Event for showing the brief creation drawer
 *
 * Expected to be triggered when the drawer is opened and step is changed (except for loading step)
 * @param currentStep
 * @param briefContextId
 * @param eventSource
 */
export const briefCreationDrawerIsShown = (
    currentStep: Step,
    briefContextId: string,
    eventSource: string,
    variant = DEFAULT_BI_PARENT_ELEMENT
) => {
    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_brief_creation_drawer',
            component: {
                impId: Date.now().toString(),
                name: 'brief_creation_drawer',
                ...sharedImpData,
            },
            organization: { id: getContext().organizationId || '' },
        },
        additionalData: {
            type: ViewedBriefCreationEducation,
            message: {
                briefContextId,
                eventSource,
                drawerType: mapStepToImpression[currentStep],
                educationType: 'none',
                parentElementType:
                    variant === 'onboarding_routing' ? ONBOARDING_ROUTING_BI_PARENT_ELEMENT : DEFAULT_BI_PARENT_ELEMENT,
            },
        },
    });
};

interface BriefCreationDrawerEducationIsShownProps {
    educationType: 'recommended_budget' | 'flow_explainer' | 'invalid_budget' | 'im_not_sure' | 'choose_routing_alert';
    briefContextId: string;
    eventSource: string;
    minimumPrice?: number;
    categoryId?: number;
    subCategoryId?: number;
    nestedCategoryId?: number;
    variant: BriefCreationVariant;
}

/**
 * Event for showing the brief creation budget education
 *
 * Drawer type is always: 'brief_editor'
 * Education type: 'recommended_budget', 'flow_explainer', 'invalid_budget'
 * For 'recommended_budget': send budget in cents
 *
 * @param BriefCreationDrawerEducationIsShownProps
 */
export const briefCreationDrawerEducationIsShown = ({
    educationType,
    minimumPrice,
    categoryId,
    subCategoryId,
    nestedCategoryId,
    briefContextId,
    eventSource,
    variant,
}: BriefCreationDrawerEducationIsShownProps) => {
    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_brief_creation_drawer_education',
            component: {
                impId: Date.now().toString(),
                name: 'brief_creation_drawer_education',
                ...sharedImpData,
            },
            organization: { id: getContext().organizationId || '' },
        },
        additionalData: {
            type: ViewedBriefCreationEducation,
            message: {
                briefContextId,
                eventSource,
                drawerType: 'brief_editor',
                categoryId,
                subCategoryId,
                nestedCategoryId,
                minimumPrice,
                educationType,
                parentElementType:
                    variant === 'onboarding_routing' ? ONBOARDING_ROUTING_BI_PARENT_ELEMENT : DEFAULT_BI_PARENT_ELEMENT,
            },
        },
    });
};

/**
 * Event for clicking on the brief input field
 */
export const briefInputClicked = (
    briefContextId: string,
    eventSource: string,
    parentElementType?: string,
    variant = DEFAULT_BI_PARENT_ELEMENT
) => {
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_brief_input_field',
            clickType: ClickType.LEFT,
            element: {
                name: 'brief_input_field',
                type: ElementType.UNSPECIFIED,
                text: { value: 'input_clicked' },
            },
            organization: { id: getContext().organizationId || '' },
        },
        additionalData: {
            type: ClickedBriefCreationDrawer,
            message: {
                drawerType: 'none',
                userAction: 'input_clicked',
                briefContextId,
                eventSource,
                parentElementType:
                    variant === 'onboarding_routing'
                        ? ONBOARDING_ROUTING_BI_PARENT_ELEMENT
                        : parentElementType || DEFAULT_BI_PARENT_ELEMENT,
            },
        },
    });
};

/**
 * Paste content on the brief input field
 */
export const briefInputPastedAction = (briefContextId: string, pastedInputLength: number) => {
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'pasted_text_brief_input',
            clickType: ClickType.UNSPECIFIED,
            element: {
                name: 'brief_input_field',
                type: ElementType.UNSPECIFIED,
                text: { value: 'input_pasted' },
            },
            organization: { id: getContext().organizationId || '' },
        },
        additionalData: {
            type: PastedBriefCreationInput,
            message: {
                briefContextId,
                pastedInputLength,
            },
        },
    });
};

interface BriefCreationClickActionProps {
    step: Step;
    briefContextId: string;
    eventSource: string;
    action:
        | 'X'
        | 'generate_brief'
        | 'post_brief'
        | 'how_does_it_work'
        | 'rephrase'
        | 'go_back'
        | 'got_it'
        | 'stay_on_fiverr'
        | 'upgrade_to_pro'
        | 'see_all_briefs'
        | 'view_brief_progress'
        | 'source_for_me'
        | 'missing_fields'
        | 'update_brief';
    input?: string;
    parentElement: string;
    variant: BriefCreationVariant;
}

/**
 * Event for clicking on the brief creation interaction buttons
 *
 * Expects to send:
 ** current step
 ** action: 'X', 'generate_brief', 'post_brief', 'how_does_it_work', 'rephrase'
 ** user input: optional
 *
 * @param BriefCreationDrawerEducationIsShownProps
 */
export const briefCreationClickAction = ({
    step,
    action,
    briefContextId,
    input,
    eventSource,
    parentElement,
    variant,
}: BriefCreationClickActionProps) => {
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_brief_creation_drawer_button',
            clickType: ClickType.LEFT,
            element: {
                name: 'brief_creation_drawer_button',
                type: ElementType.BUTTON,
                text: { value: action },
            },
            organization: { id: getContext().organizationId || '' },
        },
        additionalData: {
            type: ClickedBriefCreationDrawer,
            message: {
                drawerType: mapStepToImpression[step],
                userAction: action,
                userInput: input,
                briefContextId,
                eventSource,
                parentElementType:
                    variant === 'onboarding_routing'
                        ? ONBOARDING_ROUTING_BI_PARENT_ELEMENT
                        : parentElement || DEFAULT_BI_PARENT_ELEMENT,
            },
        },
    });
};

interface BriefEditSectionClickActionProps {
    briefContextId: string;
    sectionType: string;
    elementType: 'add_section' | 'update_section' | 'remove_section';
    elementName: string;
    elementTitle: string;
    isMandatory: boolean;
    isNotSure: boolean;
    eventSource: string;
    variant: BriefCreationVariant;
    ctaType?: BriefSectionCtaType;
}

/**
 * Event for clicking on the section actions: add, delete, save
 *
 * @param BriefEditSectionClickActionProps
 */
export const briefEditSectionClickAction = ({
    briefContextId,
    sectionType,
    elementType,
    elementName,
    elementTitle,
    isMandatory,
    isNotSure,
    variant,
    eventSource,
    ctaType,
}: BriefEditSectionClickActionProps) => {
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_brief_creation_drawer_update_element_cta',
            clickType: ClickType.LEFT,
            element: {
                name: elementName,
                type: ElementType.BUTTON,
                text: { value: elementTitle },
            },
            organization: { id: getContext().organizationId || '' },
        },
        additionalData: {
            type: ClickedBriefCreationSectionAction,
            message: {
                drawerType: 'brief_editor',
                briefContextId,
                sectionType,
                elementType,
                isMandatory,
                isNotSure,
                eventSource,
                parentElementType:
                    variant === 'onboarding_routing' ? ONBOARDING_ROUTING_BI_PARENT_ELEMENT : DEFAULT_BI_PARENT_ELEMENT,
                ctaType,
            },
        },
    });
};

/**
 * Event for viewing the brief floating form trigger
 *
 * @param pageName
 */
export const reportViewedBriefFloatingFormButtonEvent = (pageName: string) => {
    const { pageCtxId, organizationId } = getContext();

    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_brief_floating_form_button',
            ...(organizationId && { organization: { id: organizationId } }),
            page: {
                name: pageName,
                ctxId: pageCtxId,
            },
        },
    });
};

/**
 * Event for clicking the brief floating form trigger
 *
 * @param pageName
 */
export const reportClickedBriefFloatingFormButtonEvent = (pageName: string) => {
    const { pageCtxId, organizationId } = getContext();

    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_brief_floating_form_button',
            clickType: ClickType.LEFT,
            ...(organizationId && { organization: { id: organizationId } }),
            page: {
                name: pageName,
                ctxId: pageCtxId,
            },
            element: {
                name: 'brief_floating_form_button',
                type: ElementType.BUTTON,
                text: { value: 'turn your search into a brief' },
            },
        },
    });
};

interface ViewedBriefFloatingFormPopupProps {
    pageName: string;
    initialPromptText: string;
    openingPosition?: number;
    triggerScenario?: TriggerScenario;
}

/**
 * Event for viewing the brief floating form popup
 *
 * @param pageName
 * @param openingPosition
 * @param initialPromptText
 * @param triggerScenario
 */
export const reportViewedBriefFloatingFormPopupEvent = ({
    pageName,
    initialPromptText,
    openingPosition,
    triggerScenario,
}: ViewedBriefFloatingFormPopupProps) => {
    const { pageCtxId, organizationId } = getContext();

    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_brief_floating_form_popup',
            ...(organizationId && { organization: { id: organizationId } }),
            page: {
                name: pageName,
                ctxId: pageCtxId,
            },
            component: {
                ...sharedImpData,
                impId: Date.now().toString(),
                name: 'brief_floating_form_popup',
                positionInParent: openingPosition || 0,
                text: initialPromptText,
            },
        },
        additionalData: {
            type: BriefFloatingFormPopup,
            message: {
                triggeredBy:
                    (triggerScenario && mapTriggerScenarioToTriggerdBy[triggerScenario]) || TriggeredBy.MANUALLY,
            },
        },
    });
};

/**
 * Event for clicking on buttons in brief floating form popup
 *
 * @param pageName
 * @param elementText
 */
export const reportClickedBriefFloatingFormPopupEvent = (pageName: string, elementText: string) => {
    const { pageCtxId, organizationId } = getContext();

    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_brief_floating_form_popup',
            clickType: ClickType.LEFT,
            ...(organizationId && { organization: { id: organizationId } }),
            page: {
                name: pageName,
                ctxId: pageCtxId,
            },
            element: {
                name: 'brief_floating_form_popup_button',
                type: ElementType.BUTTON,
                text: { value: elementText },
            },
        },
    });
};
